import {
  APPLICATION_PROBLEM_WATCHER_HEARTBEAT_INTERVAL_MS,
  APPLICATION_PROBLEM_WATCHER_VERSION,
  browserFamilyFromUserAgent,
  createProblemEventBucket,
  createProblemEventThrottle,
  normalizeProblemWatcherFreezeThresholdMs,
  normalizeProblemWatcherThrottleMinutes,
  sanitizeProblemMessage,
  sanitizeProblemSourceUrl,
  shouldIgnoreApplicationError,
  shouldIgnoreResourceLoadFailure
} from '~/utils/applicationProblemWatcher'

type WatcherEventName = 'kendrick_application_error_failed' | 'kendrick_browser_responsiveness_failed'

type ApplicationProblemWatcherDebug = {
  reportError: (message?: unknown) => void
  reportFreeze: (durationMs?: number) => void
}

declare global {
  interface Window {
    __kendrickProblemWatcher?: ApplicationProblemWatcherDebug
  }
}

const safeNumber = (value: unknown) => {
  return typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : null
}

const safeDecimalNumber = (value: unknown) => {
  return typeof value === 'number' && Number.isFinite(value) ? Number(value.toFixed(1)) : null
}

const safePerformanceNumber = (value: unknown) => {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : null
}

export default defineNuxtPlugin((nuxtApp) => {
  if (typeof window === 'undefined') return

  const runtimeConfig = useRuntimeConfig()
  const signalsKit = useSignalsKit()
  const throttleMinutes = normalizeProblemWatcherThrottleMinutes(
    runtimeConfig.public.applicationProblemWatcherThrottleMinutes
  )
  const freezeThresholdMs = normalizeProblemWatcherFreezeThresholdMs(
    runtimeConfig.public.applicationProblemWatcherFreezeThresholdMs
  )
  const shouldSendForBucket = createProblemEventThrottle(throttleMinutes * 60 * 1000)

  const commonPayload = () => ({
    watcher_source: 'client_application_problem_watcher',
    watcher_version: APPLICATION_PROBLEM_WATCHER_VERSION,
    throttle_window_minutes: throttleMinutes,
    visibility_state: document.visibilityState,
    browser_online: navigator.onLine,
    browser_family: browserFamilyFromUserAgent(navigator.userAgent),
    browser_hardware_concurrency: navigator.hardwareConcurrency || null,
    browser_device_memory_gb: 'deviceMemory' in navigator
      ? safeDecimalNumber((navigator as Navigator & { deviceMemory?: number }).deviceMemory)
      : null,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight
  })

  const lifecyclePayload = () => {
    const now = performance.now()
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    const domContentLoadedAt = safePerformanceNumber(navigation?.domContentLoadedEventEnd)
    const loadEventAt = safePerformanceNumber(navigation?.loadEventEnd)
    const sinceLoadEventMs = loadEventAt ? Math.max(0, Math.round(now - loadEventAt)) : null
    const lifecyclePhase = document.readyState !== 'complete'
      ? 'loading'
      : sinceLoadEventMs !== null && sinceLoadEventMs < 5000
        ? 'post_load_startup'
        : 'runtime'

    return {
      document_ready_state: document.readyState,
      page_lifecycle_phase: lifecyclePhase,
      time_since_navigation_start_ms: Math.round(now),
      dom_content_loaded_at_ms: domContentLoadedAt,
      load_event_end_at_ms: loadEventAt,
      time_since_load_event_ms: sinceLoadEventMs
    }
  }

  const emitProblem = (
    eventName: WatcherEventName,
    alertBucket: string,
    throttleBucket: string,
    properties: Record<string, unknown>
  ) => {
    const throttleResult = shouldSendForBucket(throttleBucket, Date.now())
    if (!throttleResult.shouldSend) return

    try {
      signalsKit.trackFrictionSignal(eventName, {
        ...commonPayload(),
        surface: eventName === 'kendrick_browser_responsiveness_failed'
          ? 'client_performance'
          : 'client_runtime',
        action: eventName === 'kendrick_browser_responsiveness_failed'
          ? 'maintain_interactivity'
          : 'execute_client_workflow',
        alert_bucket: alertBucket,
        throttle_bucket: throttleBucket,
        suppressed_problem_count: throttleResult.suppressedCount,
        ...properties
      }, {
        kind: eventName === 'kendrick_browser_responsiveness_failed'
          ? 'browser_responsiveness'
          : 'application_error',
        severity: 'error'
      })
    } catch {
      // Analytics failures must never create recursive application-problem events.
    }
  }

  const reportApplicationError = (
    watcherKind: string,
    reason: string,
    errorLike: unknown,
    extraProperties: Record<string, unknown> = {}
  ) => {
    if (shouldIgnoreApplicationError(reason, errorLike)) return

    const source = sanitizeProblemSourceUrl(extraProperties.source_url, window.location.origin)
    const throttleBucket = createProblemEventBucket('kendrick_application_error_failed', reason, [
      watcherKind,
      extraProperties.resource_tag,
      source.source_origin,
      source.source_host,
      source.source_path,
      extraProperties.promise_reason_type,
      extraProperties.vue_error_info
    ])

    emitProblem('kendrick_application_error_failed', reason, throttleBucket, {
      watcher_kind: watcherKind,
      reason,
      error_name: errorLike instanceof Error ? errorLike.name : typeof errorLike,
      error_message: sanitizeProblemMessage(errorLike),
      ...source,
      line_number: safeNumber(extraProperties.line_number),
      column_number: safeNumber(extraProperties.column_number),
      opaque_script_error: extraProperties.opaque_script_error === true ? true : undefined,
      resource_tag: extraProperties.resource_tag,
      promise_reason_type: extraProperties.promise_reason_type,
      vue_error_info: typeof extraProperties.vue_error_info === 'string'
        ? extraProperties.vue_error_info.slice(0, 120)
        : undefined
    })
  }

  const reportBrowserResponsiveness = (
    watcherKind: string,
    reason: string,
    durationMs: number
  ) => {
    const lifecycle = lifecyclePayload()
    const throttleBucket = createProblemEventBucket('kendrick_browser_responsiveness_failed', reason, [
      watcherKind,
      lifecycle.page_lifecycle_phase
    ])

    emitProblem('kendrick_browser_responsiveness_failed', reason, throttleBucket, {
      watcher_kind: watcherKind,
      reason,
      freeze_duration_ms: Math.round(durationMs),
      freeze_threshold_ms: freezeThresholdMs,
      ...lifecycle
    })
  }

  const getResourceLoadMessage = (tagName: string, sourceUrl: string) => {
    const source = sanitizeProblemSourceUrl(sourceUrl, window.location.origin)
    const sourceLabel = source.source_path
      ? `${source.source_origin}:${source.source_path}`
      : source.source_origin

    return `${tagName}_load_failed:${sourceLabel}`
  }

  const shouldReportResourceLoadFailure = (tagName: string, sourceUrl: string) => {
    const source = sanitizeProblemSourceUrl(sourceUrl, window.location.origin)
    return !shouldIgnoreResourceLoadFailure(tagName, source)
  }

  const handleWindowError = (event: ErrorEvent | Event) => {
    if (event instanceof ErrorEvent) {
      const hasOpaqueScriptError = event.message === 'Script error.' && !event.filename

      reportApplicationError('window_error', hasOpaqueScriptError ? 'opaque_script_error' : 'uncaught_exception', event.error || event.message, {
        source_url: event.filename,
        line_number: event.lineno,
        column_number: event.colno,
        opaque_script_error: hasOpaqueScriptError
      })
      return
    }

    const target = event.target
    if (!(target instanceof HTMLElement)) return

    const tagName = target.tagName.toLowerCase()
    if (!['img', 'link', 'script'].includes(tagName)) return

    const sourceUrl = target instanceof HTMLImageElement
      ? target.currentSrc || target.src
      : target instanceof HTMLLinkElement
        ? target.href
        : target instanceof HTMLScriptElement
          ? target.src
          : ''

    if (!shouldReportResourceLoadFailure(tagName, sourceUrl)) return

    reportApplicationError('resource_error', 'resource_load_failed', getResourceLoadMessage(tagName, sourceUrl), {
      source_url: sourceUrl,
      resource_tag: tagName
    })
  }

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason
    reportApplicationError('unhandled_rejection', 'unhandled_promise_rejection', reason, {
      promise_reason_type: reason instanceof Error ? reason.name : typeof reason
    })
  }

  const previousVueErrorHandler = nuxtApp.vueApp.config.errorHandler
  nuxtApp.vueApp.config.errorHandler = (error, instance, info) => {
    reportApplicationError('vue_error', 'vue_error_handler', error, {
      vue_error_info: info
    })

    if (previousVueErrorHandler) {
      previousVueErrorHandler(error, instance, info)
    }
  }

  window.addEventListener('error', handleWindowError, true)
  window.addEventListener('unhandledrejection', handleUnhandledRejection)

  let lastHeartbeat = performance.now()
  const heartbeatTimer = window.setInterval(() => {
    const now = performance.now()
    const lagMs = now - lastHeartbeat - APPLICATION_PROBLEM_WATCHER_HEARTBEAT_INTERVAL_MS
    lastHeartbeat = now

    if (document.visibilityState !== 'visible') return
    if (lagMs >= freezeThresholdMs) {
      reportBrowserResponsiveness('event_loop_lag', 'main_thread_blocked', lagMs)
    }
  }, APPLICATION_PROBLEM_WATCHER_HEARTBEAT_INTERVAL_MS)

  const resetHeartbeat = () => {
    lastHeartbeat = performance.now()
  }
  document.addEventListener('visibilitychange', resetHeartbeat)

  let longTaskObserver: PerformanceObserver | null = null
  const supportedEntryTypes = typeof PerformanceObserver !== 'undefined'
    ? PerformanceObserver.supportedEntryTypes || []
    : []

  if (supportedEntryTypes.includes('longtask')) {
    longTaskObserver = new PerformanceObserver((list) => {
      const longestTask = list.getEntries()
        .filter(entry => entry.duration >= freezeThresholdMs)
        .sort((left, right) => right.duration - left.duration)[0]

      if (!longestTask || document.visibilityState !== 'visible') return
      reportBrowserResponsiveness('performance_longtask', 'long_task_detected', longestTask.duration)
    })
    longTaskObserver.observe({ entryTypes: ['longtask'] })
  }

  if (import.meta.dev) {
    window.__kendrickProblemWatcher = {
      reportError: (message = 'qa_problem_watcher_error') => {
        reportApplicationError('qa_manual_error', 'qa_manual_error', message)
      },
      reportFreeze: (durationMs = freezeThresholdMs + 100) => {
        reportBrowserResponsiveness('qa_manual_freeze', 'qa_manual_freeze', durationMs)
      }
    }
  }

  nuxtApp.hook('app:unmounted', () => {
    window.removeEventListener('error', handleWindowError, true)
    window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    document.removeEventListener('visibilitychange', resetHeartbeat)
    window.clearInterval(heartbeatTimer)
    longTaskObserver?.disconnect()
    if (window.__kendrickProblemWatcher) {
      delete window.__kendrickProblemWatcher
    }
  })
})
